import { Button, Input, Select, Typography } from 'antd';
import { Alert } from 'antd-v5';
import classNames from 'classnames';
import { CalendarIcon, RightArrowIcon } from 'components/Icons';
import { capitalize, compact, isString } from 'lodash';
import moment, { Moment, unitOfTime } from 'moment';
import { PickerPanel } from 'rc-picker';
import generateConfig from 'rc-picker/lib/generate/moment';
import en_US from 'rc-picker/lib/locale/en_US';
import React, { useCallback, useEffect, useState } from 'react';
import {
  RelativeTimeOptions,
  getDateTypeFromValue,
  parseDateToDisplay,
} from 'utils/dateUtils';

import ACTrigger from 'ac-components/ACTrigger';
import ArrowRightLeftIcon from 'ac-components/ac-icons/ArrowRightLeftIcon';

import './index.less';

export type Value = React.Key | undefined; /* string | number | undefined */
export type Values = Value[] | undefined;

type Props = {
  value?: Values;
  onChange: (value: Values) => void;
};
const defaultValue: Values = [Date.now() - 1000 * 60 * 60 * 24 * 70, '1 s+'];

export default function ACDateRangeFilter({
  value = defaultValue,
  onChange,
}: Props) {
  const [activeDate, setActiveDate] = useState<ActiveButton>();
  const [popupVisible, setPopupVisible] = useState(false);
  const [tempValues, setTempValues] = useState<Values>(value);

  useEffect(() => {
    setTempValues(value);
  }, [value]);

  const _onChange = useCallback(
    (value?: Value) => {
      setTempValues((prev) => {
        if (!value) {
          return undefined;
        }
        if (activeDate === 'start') {
          return [value, prev?.[1]];
        } else {
          return [prev?.[0], value];
        }
      });
    },
    [activeDate],
  );

  return (
    <ACTrigger
      placement={'bottom'}
      trigger={'click'}
      popupVisible={popupVisible}
      onPopupVisibleChange={(visible) => {
        setPopupVisible(visible);
        setActiveDate(visible ? 'start' : undefined);
      }}
      popup={
        <div className="ac-date-range-calendar-popup-container">
          <ACDateSelector
            activeDate={activeDate}
            value={tempValues}
            onChange={_onChange}
          />
        </div>
      }
    >
      <div className="ac-date-range-button-trigger">
        <ACDateButtons
          activeDate={activeDate}
          onChange={(activeDate) => {
            setPopupVisible(true);
            setActiveDate(activeDate);
          }}
          value={tempValues}
        />
      </div>
    </ACTrigger>
  );
}

export type ActiveButton = 'start' | 'end';

export function ACDateButtons({
  activeDate,
  onChange,
  onSwap,
  value,
}: {
  activeDate?: ActiveButton;
  onChange: (activeDate: ActiveButton) => void;
  onSwap?: () => void;
  value?: Values;
}) {
  const onClick = (e: React.MouseEvent<HTMLDivElement>) => {
    e.stopPropagation();
    onChange(e.currentTarget.dataset.key as ActiveButton);
  };
  return (
    <div className="ac-date-range-button-container">
      <div className="calendar-icon">
        <CalendarIcon />
      </div>
      <div
        className={classNames('ac-date-range-button ac-date-range-start', {
          active: activeDate === 'start',
        })}
        data-key="start"
        onClick={onClick}
      >
        {value?.[0] ? parseDateToDisplay(value[0]) : 'Start date'}
      </div>
      <div className="arrow-icon" onClick={onSwap}>
        <RightArrowIcon className="right-arrow-icon" />
        <ArrowRightLeftIcon className="arrow-right-left-icon" />
      </div>
      <div
        className={classNames('ac-date-range-button ac-date-range-end', {
          active: activeDate === 'end',
        })}
        data-key="end"
        onClick={onClick}
      >
        {value?.[1] ? parseDateToDisplay(value[1]) : 'End date'}
      </div>
    </div>
  );
}

export type DateType = 'absolute' | 'relative' | 'now';

export function ACDateSelector({
  activeDate,
  value,
  onChange,
  onApply,
  onClear,
  valueChanged,
}: {
  activeDate?: ActiveButton;
  value?: Values;
  onChange: (value?: Value, type?: DateType) => void;
  onApply?: () => void;
  onClear?: () => void;
  valueChanged?: boolean;
}) {
  const [currentTab, setCurrentTab] = useState<DateType>('relative');
  const [showError, setShowError] = useState<boolean>(false);
  const tabs: DateType[] = ['relative', 'now', 'absolute'];
  const tempValue = activeDate === 'start' ? value?.[0] : value?.[1];

  const updateTempValue = (value: Value) => {
    onChange(value, currentTab);
  };

  useEffect(() => {
    if (value && compact(value).length === 2) {
      let _startDate = value[0];
      let _endDate = value[1];

      if (_startDate && _endDate) {
        if (typeof _startDate === 'string') {
          _startDate = convertRelativeToAbsolute(_startDate);
        }
        if (typeof _endDate === 'string') {
          _endDate = convertRelativeToAbsolute(_endDate);
        }
        setShowError(_endDate < _startDate);
      }
    } else {
      setShowError(false);
    }
  }, [value]);

  const onTabChange = (e: React.MouseEvent<HTMLDivElement>) => {
    const tab = e.currentTarget.dataset.key as DateType;
    setCurrentTab(tab);
    if (tab === 'now') {
      updateTempValue('now');
    } else if (tempValue) {
      if (currentTab === 'absolute' && tab === 'relative') {
        updateTempValue(convertAbsoluteToRelative(tempValue));
      } else if (currentTab === 'now' && tab === 'relative') {
        updateTempValue('0 d');
      } else if (currentTab === 'relative' && tab === 'absolute') {
        updateTempValue(convertRelativeToAbsolute(tempValue, activeDate));
      } else if (currentTab === 'now' && tab === 'absolute') {
        const method = activeDate === 'start' ? 'startOf' : 'endOf';
        updateTempValue(moment()[method]('D').toDate().getTime());
      }
    }
  };

  useEffect(() => {
    if (value && activeDate) {
      setCurrentTab(getDateTypeFromValue(tempValue));
    }
  }, [value, activeDate, tempValue]);

  const [relativeCount = '0', relativeDuration = 'd'] =
    tempValue?.toString().split(' ') || [];

  const onRelativeCountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    updateTempValue(`${e.currentTarget.value} ${relativeDuration}`);
  };

  const onRelativeDurationChange = (e: string) => {
    updateTempValue(`${relativeCount} ${e}`);
  };

  return (
    <div className="ac-date-range-calendar-container">
      <div className="ac-date-range-calendar-tabs">
        {tabs.map((tab) => (
          <div
            key={tab}
            onClick={onTabChange}
            className={classNames('ac-date-range-calendar-tab', {
              active: currentTab === tab,
            })}
            data-key={tab}
          >
            {capitalize(tab)}
          </div>
        ))}
      </div>
      {currentTab === 'absolute' ? (
        <PickerPanel<Moment>
          // @ts-ignore - className is accepted but not defined in type
          className="border-none border-bottom"
          onChange={(date) => {
            const dateValue = date as Moment;
            const _date =
              activeDate === 'start'
                ? dateValue.startOf('D')
                : dateValue.endOf('D');
            updateTempValue(_date.toDate().getTime());
          }}
          generateConfig={generateConfig}
          value={tempValue ? moment(tempValue) : undefined}
          locale={en_US}
          prefixCls={'ant-picker'}
        />
      ) : currentTab === 'relative' ? (
        <div>
          <div className="d-flex m-l m-b-m">
            <Input
              type="number"
              style={{ width: 90 }}
              value={relativeCount}
              defaultValue={0}
              onKeyDown={(e) => {
                if (
                  e.key === '.' ||
                  e.key === '-' ||
                  e.key.toLowerCase() === 'e'
                )
                  e.preventDefault();
              }}
              min={0}
              onChange={onRelativeCountChange}
            />
            <Select
              options={RelativeTimeOptions}
              className="flex-1 m-l-m"
              value={relativeDuration}
              onChange={onRelativeDurationChange}
            />
          </div>
          <div className="ac-date-range-relative-container d-flex justify-content-center align-items-center">
            {formatValue(value?.[0]) || 'Start Date'}-
            {formatValue(value?.[1]) || 'End Date'}
          </div>
        </div>
      ) : (
        <div className="p-l p-b-m">
          <Typography.Text>This will set to the current day</Typography.Text>
          {/* <Button block type="primary" className="m-t-sm">
            Set end date to today
          </Button> */}
        </div>
      )}
      {showError ? (
        <Alert
          className="p-x-sm p-y-sm"
          description={
            'Invalid Date Range. The first date should be earlier than the last date'
          }
          type="error"
        />
      ) : null}
      <div className="d-flex p-x-xs p-b-xs p-t-xs justify-content-between border-top-light">
        <Button className="p-x-0" onClick={onClear} size="small" type="link">
          Clear
        </Button>
        <Button
          onClick={onApply}
          type="primary"
          size="small"
          disabled={
            compact(value).length !== 2 || showError || valueChanged === false
          }
        >
          Apply
        </Button>
      </div>
    </div>
  );
}

export const isRelativeTime = (value: string): boolean => {
  const relativeTimePattern = /^\d+\s*[hdwMy]\+?$/;
  return relativeTimePattern.test(value);
};

export const formatValue = (value: Value) => {
  if (typeof value === 'string') {
    if (isRelativeTime(value)) {
      return moment(convertRelativeToAbsolute(value)).format('MM/DD/YYYY');
    } else {
      return capitalize(value);
    }
  } else if (value !== undefined) {
    return moment(value).format('MM/DD/YYYY');
  }
  return value;
};

export const convertAbsoluteToRelative = (value?: Value) => {
  if (value) {
    const str = moment(value)
      .fromNow()
      .replace('days', 'd')
      .replace('day', 'd')
      .replace('weeks', 'w')
      .replace('week', 'w')
      .replace('months', 'M')
      .replace('month', 'M')
      .replace('years', 'y')
      .replace('year', 'y');

    if (str.includes('hour') || str.includes('min') || str.includes('sec')) {
      return '0 d';
    }
    if (str.startsWith('in')) {
      return str.replace('in', '').trim() + '+';
    }
    if (str.endsWith('ago')) {
      return str.replace('ago', '').trim();
    }
  }
  return '0 d';
};
export const convertRelativeToAbsolute = (
  value?: Value,
  activeDate?: ActiveButton,
) => {
  if (value && isString(value)) {
    let date = moment();
    const [count, unit] = value.split(' ');
    if (count && unit) {
      if (value.endsWith('+')) {
        date = date.add(+count, unit[0] as unitOfTime.DurationConstructor);
      } else {
        date = date.subtract(+count, unit[0] as unitOfTime.DurationConstructor);
      }
      // if relative time is less than a day then don't convert to start/end of day
      if ('h' !== unit[0]) {
        if (activeDate === 'end') {
          date = date.endOf('D');
        } else {
          date = date.startOf('D');
        }
      }
    }
    return date.toDate().getTime();
  }
  return Date.now();
};

// const convertDateToStateValue = (value: Value, type: DateType) => {
//   switch(type){
//     case "absolute":
//       return moment(value);
//     case "relative":
//       return moment(value);
//   }
// }
